import { useCallback } from "react";

const STORAGE_KEY = "waLoginRedirectPath";
const SEARCH_PARAMS_STORAGE_KEY = "waLoginRedirectSearchParams";

const toUrlSearchParams = (search: string) =>
  new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

/** Strip auth-only params; keep deep-link params like broadcast. */
export const stripAuthSearchParams = (params: URLSearchParams) => {
  params.delete("email");
  params.delete("code");
  params.delete("state");
  return params;
};

/** Strip auth-only params and persist deep-link query params for post-login restore. */
export const mergeLoginRedirectSearchParamsFromLocation = (
  locationSearch: string,
) => {
  if (!locationSearch || locationSearch.length <= 1) return;

  const params = stripAuthSearchParams(toUrlSearchParams(locationSearch));

  const preserved = params.toString();
  if (!preserved) return;

  sessionStorage.setItem(SEARCH_PARAMS_STORAGE_KEY, `?${preserved}`);
};

/** Merge broadcast / resource / organizationId / editProfile (+ membership intent) into stored post-login search params. */
export const mergeLoginRedirectDeepLinkParams = (deepLink: {
  broadcast?: string | null;
  resource?: string | null;
  organizationId?: string | null;
  editProfile?: string | null;
  intent?: string | null;
  membership?: string | null;
}) => {
  const existing = sessionStorage.getItem(SEARCH_PARAMS_STORAGE_KEY);
  const params = toUrlSearchParams(existing ?? "");

  if (deepLink.broadcast) {
    params.set("broadcast", deepLink.broadcast);
  }
  if (deepLink.resource) {
    params.set("resource", deepLink.resource);
  }
  if (deepLink.organizationId) {
    params.set("organizationId", deepLink.organizationId);
  }
  if (deepLink.editProfile) {
    params.set("editProfile", deepLink.editProfile);
  }
  if (deepLink.broadcast && deepLink.intent) {
    params.set("intent", deepLink.intent);
    if (deepLink.membership) params.set("membership", deepLink.membership);
  }

  const preserved = params.toString();
  if (preserved) {
    sessionStorage.setItem(SEARCH_PARAMS_STORAGE_KEY, `?${preserved}`);
  }
};

export const getLoginRedirectSearchParamsFromStorage = () =>
  sessionStorage.getItem(SEARCH_PARAMS_STORAGE_KEY);

/**
 * Remove a single deep-link key from the stored post-login search params.
 *
 * The auth/onboarding flow keeps `broadcast` / `organizationId` in sessionStorage
 * so they survive the Auth0 round-trip (see `useAuth0WithNavigate`). The
 * restore effect re-adds anything in storage to the URL on `/app*`, which
 * means in-app actions that intentionally remove a param (e.g. closing a
 * broadcast modal) also need to drop it from storage — otherwise the next
 * render restores it and the modal reopens.
 */
export const clearLoginRedirectDeepLinkParam = (key: string) => {
  const existing = sessionStorage.getItem(SEARCH_PARAMS_STORAGE_KEY);
  if (!existing) return;
  const params = toUrlSearchParams(existing);
  if (!params.has(key)) return;
  params.delete(key);
  const remaining = params.toString();
  if (remaining) {
    sessionStorage.setItem(SEARCH_PARAMS_STORAGE_KEY, `?${remaining}`);
  } else {
    sessionStorage.removeItem(SEARCH_PARAMS_STORAGE_KEY);
  }
};

export const useLoginRedirectPath = () => {
  const setLoginRedirectPath = useCallback(
    (path: string) => sessionStorage.setItem(STORAGE_KEY, path),
    [],
  );
  const getLoginRedirectPath = useCallback(
    () => sessionStorage.getItem(STORAGE_KEY),
    [],
  );
  const clearLoginRedirectPath = useCallback(
    () => sessionStorage.removeItem(STORAGE_KEY),
    [],
  );

  const setLoginRedirectSearchParams = useCallback(
    (searchParams: string) =>
      sessionStorage.setItem(SEARCH_PARAMS_STORAGE_KEY, searchParams),
    [],
  );
  const getLoginRedirectSearchParams = useCallback(
    () => sessionStorage.getItem(SEARCH_PARAMS_STORAGE_KEY),
    [],
  );
  const clearLoginRedirectSearchParams = useCallback(
    () => sessionStorage.removeItem(SEARCH_PARAMS_STORAGE_KEY),
    [],
  );

  return {
    getLoginRedirectPath,
    setLoginRedirectPath,
    clearLoginRedirectPath,
    getLoginRedirectSearchParams,
    setLoginRedirectSearchParams,
    clearLoginRedirectSearchParams,
  };
};
